/* ============================================================
   Flow Wake-Up Quiz — Screens A
   Exposes: LandingScreen, QuestionScreen, InterstitialScreen
   ============================================================ */

(function () {
  var jsx = window.jsx;
  var useState = React.useState;
  var useEffect = React.useEffect;
  var useCallback = React.useCallback;
  var useRef = React.useRef;
  var tapDemoPressLockUntil = 0;
  var tapDemoTailTimer = null;
  var tapDemoTailEvents = [
    'pointerdown',
    'pointerup',
    'touchstart',
    'touchend',
    'touchcancel',
    'mousedown',
    'mouseup',
    'click',
    'auxclick',
    'dblclick'
  ];
  var tapDemoTailListenerOptions = { capture: true, passive: false };

  // Segments-with-accent helpers. Lets content.js mark phrases for
  // orange (optionally underlined) emphasis without each renderer
  // re-implementing the same wrapping.
  function toSegments(value) {
    if (Array.isArray(value)) return value;
    return [{ text: value == null ? '' : String(value) }];
  }
  function segmentsString(segments) {
    var out = '';
    for (var i = 0; i < segments.length; i++) out += segments[i].text || '';
    return out;
  }
  function segmentClass(seg) {
    if (!seg.accent) return undefined;
    return 'quiz-accent' + (seg.underline ? ' quiz-accent--underline' : '');
  }
  // Hand-drawn marker-style underline. ViewBox is stretched horizontally to
  // fit any phrase width; the path uses two smooth Béziers so it reads as a
  // sweep instead of a CSS link line. Stroke uses currentColor so the line
  // picks up the segment's accent color.
  function renderUnderlineSvg() {
    return jsx('svg', {
      className: 'quiz-accent__underline-svg',
      'aria-hidden': true,
      viewBox: '0 0 240 10',
      preserveAspectRatio: 'none',
      children: jsx('path', {
        // Nearly-flat brush stroke with a very gentle lift in the middle —
        // reads as a hand-drawn underline without the cartoonish wobble.
        d: 'M 3 7 C 80 5.6, 160 5.6, 237 7',
        stroke: 'currentColor',
        strokeWidth: 3,
        strokeLinecap: 'round',
        fill: 'none'
      })
    });
  }
  function renderSegments(segments) {
    return segments.map(function (seg, idx) {
      var cls = segmentClass(seg);
      if (seg.accent && seg.underline) {
        return jsx('span', {
          key: 'seg' + idx,
          className: cls,
          children: [seg.text || '', renderUnderlineSvg()]
        });
      }
      return jsx('span', { key: 'seg' + idx, className: cls, children: seg.text || '' });
    });
  }
  function renderTypedSegments(segments, typedCount) {
    var consumed = 0;
    var nodes = [];
    for (var i = 0; i < segments.length; i++) {
      var remaining = typedCount - consumed;
      if (remaining <= 0) break;
      var text = segments[i].text || '';
      var visible = remaining >= text.length ? text : text.slice(0, remaining);
      if (visible) {
        nodes.push(jsx('span', {
          key: 'seg' + i,
          className: segmentClass(segments[i]),
          children: visible
        }));
      }
      consumed += text.length;
    }
    return nodes;
  }

  function stopTapDemoEvent(event) {
    if (!event) return;
    if (event.preventDefault) event.preventDefault();
    if (event.stopPropagation) event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    if (event.nativeEvent && event.nativeEvent.stopImmediatePropagation) {
      event.nativeEvent.stopImmediatePropagation();
    }
  }

  function swallowTapDemoTailEvent(event) {
    stopTapDemoEvent(event);
  }

  function releaseTapDemoTailSwallow() {
    tapDemoTailEvents.forEach(function (eventName) {
      document.removeEventListener(eventName, swallowTapDemoTailEvent, tapDemoTailListenerOptions);
    });
    if (tapDemoTailTimer) {
      window.clearTimeout(tapDemoTailTimer);
      tapDemoTailTimer = null;
    }
  }

  function swallowTapDemoTail(ms) {
    releaseTapDemoTailSwallow();
    tapDemoTailEvents.forEach(function (eventName) {
      document.addEventListener(eventName, swallowTapDemoTailEvent, tapDemoTailListenerOptions);
    });
    tapDemoTailTimer = window.setTimeout(releaseTapDemoTailSwallow, ms);
  }

  function claimTapDemoPress(event) {
    stopTapDemoEvent(event);
    var now = window.performance && typeof window.performance.now === 'function'
      ? window.performance.now()
      : Date.now();
    if (now < tapDemoPressLockUntil) return false;
    // A single physical tap may emit pointer, touch, mouse, and click events.
    // Hold a short cross-screen lock so the tail of that tap cannot activate
    // the freshly-mounted next demo screen.
    tapDemoPressLockUntil = now + 800;
    swallowTapDemoTail(800);
    return true;
  }

  function haptic(method, options) {
    var h = window.FlowHaptics;
    if (h && typeof h[method] === 'function') h[method](options);
  }

  // ------------------------------------------------------------
  // LandingScreen
  // ------------------------------------------------------------
  function LandingScreen(props) {
    var heroVideo = 'https://flowalarmclock.com/cdn/shop/videos/c/vp/2b4709187f36418ca8bb13b481425dcb/2b4709187f36418ca8bb13b481425dcb.HD-1080p-4.8Mbps-80285654.mp4?v=0';
    var heroPoster = 'https://flowalarmclock.com/cdn/shop/files/hero_animation_first_frame.png?v=1775332566&width=1200';
    var assignment = props.landingAssignment || {};
    var isRightForYou = assignment.variantKey === 'right_for_you';

    function handleStart() {
      haptic('advance');
      props.onStart();
    }

    function renderHeroMedia(className) {
      return jsx('div', {
        className: className || 'landing__image-wrap',
        children: [
          jsx('video', {
            className: 'landing__image',
            autoPlay: true,
            loop: true,
            muted: true,
            playsInline: true,
            preload: 'auto',
            poster: heroPoster,
            children: jsx('source', { src: heroVideo, type: 'video/mp4' })
          })
        ]
      });
    }

    if (isRightForYou) {
      return jsx('div', {
        className: 'screen landing landing--right-for-you',
        children: [
          jsx('div', { className: 'landing__ambient', 'aria-hidden': true }),
          jsx('div', {
            className: 'landing__hero landing__hero--right-for-you',
            children: [
              jsx('div', {
                className: 'landing__copy landing__copy--right-for-you',
                children: [
                  jsx('h1', {
                    className: 'landing__title landing__title--right-for-you',
                    children: [
                      'Is Flow ',
                      jsx('span', {
                        className: 'landing__title-highlight',
                        children: 'Right For You'
                      }),
                      '?'
                    ]
                  }),
                  jsx('p', {
                    className: 'landing__subtitle landing__subtitle--right-for-you',
                    children: 'Flow is the alarm that forces you out of bed & gain control of your day before it begins.'
                  })
                ]
              }),
              renderHeroMedia('landing__image-wrap landing__image-wrap--right-for-you'),
              jsx('div', {
                className: 'landing__stakes',
                children: [
                  jsx('h2', {
                    className: 'landing__stakes-title',
                    children: [
                      'At Stake: ',
                      jsx('span', {
                        className: 'landing__offer-text',
                        children: '67% OFF'
                      })
                    ]
                  }),
                  jsx('p', {
                    className: 'landing__stakes-copy',
                    children: "Take 60 seconds to find out if Flow's right for you (and unlock your bonuses)"
                  })
                ]
              })
            ]
          }),
          jsx('div', {
            className: 'cta-row cta-row--sticky landing__cta-row',
            children: jsx('button', {
              className: 'btn btn--primary landing__cta',
              onClick: handleStart,
              children: 'Start Flow Quiz'
            })
          })
        ]
      });
    }

    return jsx('div', {
      className: 'screen landing',
      children: [
        // Background ambient glow that pulses behind the hero.
        jsx('div', { className: 'landing__ambient', 'aria-hidden': true }),

        jsx('div', {
          className: 'landing__hero',
          children: [
            jsx('div', {
              className: 'landing__copy',
              children: [
                jsx('h1', {
                  className: 'landing__title',
                  children: [
                    jsx('span', { className: 'landing__title-top', children: 'Win your morning.' }),
                    jsx('span', { className: 'landing__title-accent', children: 'Win your day.' })
                  ]
                }),
                jsx('p', {
                  className: 'landing__subtitle',
                  children: 'Flow is the alarm that forces you out of bed with over 100 alarm sounds.'
                })
              ]
            }),

            renderHeroMedia()
          ]
        }),

        // Sticky CTA at the bottom matching the rest of the funnel
        jsx('div', {
          className: 'cta-row cta-row--sticky landing__cta-row',
          children: [
            jsx('div', {
              className: 'landing__proof-strip',
              children: [
                jsx('span', { className: 'landing__proof-stars', 'aria-hidden': true, children: '★★★★★' }),
                jsx('span', { children: '4.8/5 rated by heavy snoozers' })
              ]
            }),
            jsx('button', {
              className: 'btn btn--primary landing__cta',
              onClick: handleStart,
              children: 'Tell me more 👀'
            })
          ]
        })
      ]
    });
  }
  window.LandingScreen = LandingScreen;

  // ------------------------------------------------------------
  // QuestionScreen — handles both 'single' and 'multi'
  // Single: auto-advances on pick (after a 140ms confirm beat)
  // Multi:  user picks up to maxPicks, then taps Continue
  // ------------------------------------------------------------
  function QuestionScreen(props) {
    var q = props.question;
    var isMulti = q.type === 'multi';
    var isSlider = q.type === 'slider';

    // For single: selected = index|null. For multi: selected = array of indices.
    // For slider: selected = numeric value.
    var stateRef = useState(function () {
      if (isMulti) return Array.isArray(props.initialValue) ? props.initialValue : [];
      if (isSlider) return typeof props.initialValue === 'number' ? props.initialValue : (q.defaultValue || q.min || 0);
      return typeof props.initialValue === 'number' ? props.initialValue : null;
    });
    var selected = stateRef[0];
    var setSelected = stateRef[1];

    // Single-select locks once picked. Without this lock, a second tap during
    // the 140ms confirm window schedules a second onAnswer → advance(), which
    // would skip a question.
    var lockedRef = React.useRef(false);
    var sliderHapticStepRef = useRef(Math.floor(Number(selected || 0) / 5));

    var pick = useCallback(function (idx) {
      if (isMulti) {
        setSelected(function (curr) {
          var arr = Array.isArray(curr) ? curr.slice() : [];
          var pos = arr.indexOf(idx);
          if (pos >= 0) {
            arr.splice(pos, 1);
            haptic('back', { key: 'multi-deselect' });
          } else {
            if (arr.length >= (q.maxPicks || 99)) {
              // Replace the oldest pick to honor maxPicks cap.
              arr.shift();
              haptic('motion', { key: 'multi-replace' });
            } else {
              haptic('selection', { key: 'multi-select' });
            }
            arr.push(idx);
          }
          return arr;
        });
      } else if (!isSlider) {
        if (lockedRef.current) return;
        lockedRef.current = true;
        haptic('selection', { key: 'single-answer' });
        if (q.id === 'wake_time') {
          try { sessionStorage.setItem('flow-wake-time-answer', String(idx)); } catch (e) {}
        }
        setSelected(idx);
        // Small confirm beat so the selected ring is visible before advance.
        setTimeout(function () {
          haptic('advance', { key: 'single-answer-advance' });
          props.onAnswer(idx);
        }, 140);
      }
    }, [isMulti, isSlider, q.maxPicks, props.onAnswer]);

    window.useKeyHotkeys(isSlider ? 0 : q.options.length, pick);

    function isSelected(idx) {
      if (isMulti) return Array.isArray(selected) && selected.indexOf(idx) >= 0;
      return selected === idx;
    }

    var canContinue = isMulti && Array.isArray(selected) && selected.length > 0;
    // Compact mode = anything with 5 or more options (Q7, Q9). On a 700px
    // phone these otherwise overflow even with the sticky CTA.
    var isCompact = q.options && q.options.length >= 5;
    var sliderLabel = selected >= (q.max || 60) ? (q.max || 60) + '+m' : selected + 'm';
    var sliderIsAverage = isSlider && selected === q.defaultValue;

    function handleSliderInput(e) {
      var nextValue = Number(e.target.value);
      var detent = Math.floor(nextValue / 5);
      if (detent !== sliderHapticStepRef.current) {
        sliderHapticStepRef.current = detent;
        haptic('selection', { key: 'slider-detent', minInterval: 65 });
      }
      setSelected(nextValue);
    }

    function submitSlider() {
      haptic('advance', { key: 'slider-continue' });
      props.onAnswer(selected);
    }

    function submitMulti() {
      if (!canContinue) {
        haptic('warning', { key: 'multi-disabled' });
        return;
      }
      haptic('advance', { key: 'multi-continue' });
      props.onAnswer(selected);
    }

    return jsx('div', {
      className: 'screen question-screen',
      children: [
        jsx('div', {
          className: 'question-screen__header',
          children: [
            // Eyebrow + description removed — clean Figma-style title only.
            jsx('h1', { className: 'q-title', children: q.title })
          ]
        }),

        isSlider ? jsx('div', {
          className: 'slider-answer',
          children: [
            jsx('div', {
              className: 'slider-answer__value',
              children: [
                jsx('span', { children: sliderLabel }),
                sliderIsAverage ? jsx('span', { className: 'slider-answer__average', children: 'average' }) : null
              ]
            }),
            jsx('input', {
              className: 'slider-answer__input',
              type: 'range',
              min: q.min || 0,
              max: q.max || 60,
              step: q.step || 1,
              value: selected,
              onInput: handleSliderInput,
              onChange: handleSliderInput,
              'aria-label': q.title,
              'aria-valuetext': sliderLabel
            }),
            jsx('div', {
              className: 'slider-answer__scale',
              'aria-hidden': true,
              children: [
                jsx('span', { children: '0' }),
                jsx('span', { children: '60+' })
              ]
            })
          ]
        }) : jsx('div', {
          className: 'options' + (isCompact ? ' options--compact' : ''),
          children: q.options.map(function (opt, idx) {
            return jsx(window.OptionButton, {
              key: q.id + '-' + idx,
              index: idx,
              label: opt.label,
              detail: opt.detail,
              selected: isSelected(idx),
              autoFocus: idx === 0,
              onClick: function () { pick(idx); }
            });
          })
        }),

        isSlider
          ? jsx('div', {
              className: 'cta-row cta-row--sticky',
              children: jsx('button', {
                className: 'btn btn--primary',
                onClick: submitSlider,
                children: 'Continue'
              })
            })
          : isMulti
          ? jsx('div', {
              className: 'cta-row cta-row--sticky',
              onPointerDown: function () {
                if (!canContinue) haptic('warning', { key: 'multi-disabled' });
              },
              children: jsx('button', {
                className: 'btn btn--primary',
                disabled: !canContinue,
                onClick: submitMulti,
                children: 'Continue'
              })
            })
          : null
      ]
    });
  }
  window.QuestionScreen = QuestionScreen;

  // ------------------------------------------------------------
  // InterstitialScreen — variant dispatcher
  // Variants:
  //   statShock  — three escalating animated number cards
  //   reframe    — strike-out list + punchline reveal
  //   timeline   — vertical timeline of {time, text} rows
  //   (default)  — legacy text-only fallback (kept for safety)
  // ------------------------------------------------------------
  function InterstitialScreen(props) {
    var i = props.interstitial;
    if (i.variant === 'situationIntro')   return jsx(SituationIntroInterstitial,   { interstitial: i, onContinue: props.onContinue }, i.id);
    if (i.variant === 'answerStat')       return jsx(AnswerStatInterstitial,       { interstitial: i, answers: props.answers, onContinue: props.onContinue });
    if (i.variant === 'image')             return jsx(ImageInterstitial,             { interstitial: i, onContinue: props.onContinue });
    if (i.variant === 'appBreakdown')      return jsx(AppBreakdownInterstitial,      { interstitial: i, onContinue: props.onContinue });
    if (i.variant === 'flowDemoAlarm')     return jsx(FlowDemoAlarmInterstitial,     { interstitial: i, answers: props.answers, navigationDirection: props.navigationDirection, onContinue: props.onContinue });
    if (i.variant === 'alarmSounds')       return jsx(AlarmSoundsInterstitial,       { interstitial: i, onContinue: props.onContinue });
    if (i.variant === 'commitment')        return jsx(CommitmentInterstitial,        { interstitial: i });
    if (i.variant === 'morningWithFlow')   return jsx(MorningWithFlowInterstitial,   { interstitial: i, answers: props.answers, onContinue: props.onContinue });
    if (i.variant === 'statShock')         return jsx(StatShockInterstitial,         { interstitial: i, onContinue: props.onContinue });
    if (i.variant === 'reframe')     return jsx(ReframeInterstitial,     { interstitial: i, onContinue: props.onContinue });
    if (i.variant === 'timeline')    return jsx(TimelineInterstitial,    { interstitial: i, onContinue: props.onContinue });
    if (i.variant === 'cycle')       return jsx(CycleInterstitial,       { interstitial: i, onContinue: props.onContinue });
    if (i.variant === 'comparison')  return jsx(ComparisonInterstitial,  { interstitial: i, onContinue: props.onContinue });
    if (i.variant === 'bestVersion') return jsx(BestVersionInterstitial, { interstitial: i, answers: props.answers, onContinue: props.onContinue });
    if (i.variant === 'youVsFlow')   return jsx(YouVsFlowInterstitial,   { interstitial: i, onContinue: props.onContinue });
    if (i.variant === 'graph')       return jsx(GraphInterstitial,       { interstitial: i, answers: props.answers, onContinue: props.onContinue });
    if (i.variant === 'barChart')    return jsx(SnoozeBarsInterstitial,   { interstitial: i, onContinue: props.onContinue });

    return jsx('div', {
      className: 'screen interstitial',
      children: [
        i.kicker ? jsx('div', { className: 'interstitial__kicker', children: i.kicker }) : null,
        jsx('h2', { className: 'interstitial__title', children: i.title }),
        i.body ? jsx('p', { className: 'interstitial__body', children: i.body }) : null,
        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            className: 'btn btn--primary btn--light',
            onClick: function () { haptic("advance"); props.onContinue(); },
            children: i.cta || 'Continue'
          })
        })
      ]
    });
  }
  window.InterstitialScreen = InterstitialScreen;

  function CommitmentInterstitial(props) {
    var i = props.interstitial;
    var titleSegments = toSegments(i.title);
    var titleText = segmentsString(titleSegments);
    var titleLength = titleText.length;
    var countRef = useState(0);
    var typedCount = countRef[0];
    var setTypedCount = countRef[1];
    var committedRef = useRef(false);
    var isComplete = typedCount >= titleLength;

    useEffect(function () {
      if (isComplete) return undefined;
      var t = setInterval(function () {
        setTypedCount(function (count) {
          if (count < titleLength) haptic('selection', { key: 'commit-type-char', minInterval: 0 });
          return Math.min(count + 1, titleLength);
        });
      }, 64);
      return function () { clearInterval(t); };
    }, [isComplete, titleLength]);

    function handleCommit() {
      var redirectUrl;
      var sequence;
      var stepIndex;
      var analyticsProps;

      if (!isComplete) {
        haptic('warning', { key: 'commit-incomplete' });
        setTypedCount(titleLength);
        return;
      }
      if (committedRef.current) return;
      committedRef.current = true;

      haptic('success', { key: 'commit-complete' });

      redirectUrl = i.href || 'https://flowalarmclock.com/products/flow-alarm';
      sequence = window.QUIZ && window.QUIZ.sequence;
      if (Array.isArray(sequence)) {
        sequence.some(function (step, index) {
          if (!step || step.kind !== 'interstitial' || step.id !== i.id) return false;
          stepIndex = index;
          return true;
        });
      }

      analyticsProps = {
        step_id: i.id || 'inter_commitment_final',
        step_index: stepIndex,
        step_type: 'interstitial',
        offer_url: redirectUrl
      };

      if (window.trackEvent) {
        try {
          window.trackEvent('step_completed', analyticsProps);
          window.trackEvent('quiz_completed', analyticsProps);
        } catch (e) { /* analytics never blocks checkout handoff */ }
      }

      window.setTimeout(function () {
        window.location.href = redirectUrl;
      }, 125);
    }

    return jsx('div', {
      className: 'screen commitment-screen',
      children: [
        jsx('div', {
          className: 'commitment-screen__copy',
          children: jsx('h1', {
            className: 'commitment-screen__title',
            'aria-live': 'polite',
            children: renderTypedSegments(titleSegments, typedCount)
          })
        }),
        jsx('div', {
          className: 'cta-row cta-row--sticky commitment-screen__cta' + (isComplete ? ' is-visible' : ''),
          children: jsx('button', {
            className: 'btn btn--primary btn--light',
            disabled: !isComplete,
            onClick: handleCommit,
            children: i.cta || 'Commit'
          })
        })
      ]
    });
  }

  function SituationIntroInterstitial(props) {
    var i = props.interstitial;
    var titleSegments = toSegments(i.title);
    var titleText = segmentsString(titleSegments);
    var titleLength = titleText.length;
    var countRef = useState(0);
    var typedCount = countRef[0];
    var setTypedCount = countRef[1];
    var exitingRef = useState(false);
    var isExiting = exitingRef[0];
    var setIsExiting = exitingRef[1];
    var intervalRef = useRef(null);
    var advancingRef = useRef(false);
    var completedOnPressRef = useRef(false);
    var completedOnPressTimerRef = useRef(null);
    var isComplete = isExiting || typedCount >= titleLength;
    var displayCount = isComplete ? titleLength : typedCount;

    useEffect(function () {
      if (isExiting) return;
      setTypedCount(0);
    }, [titleText, isExiting]);

    useEffect(function () {
      if (isComplete) return undefined;
      intervalRef.current = setInterval(function () {
        setTypedCount(function (count) {
          if (count < titleLength) haptic('selection', { key: 'situation-type-char', minInterval: 0 });
          return Math.min(count + 1, titleLength);
        });
      }, 62);
      return function () {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }, [isComplete, titleLength]);

    function freezeTyping(event) {
      haptic('motion', { key: 'typing-freeze', minInterval: 260 });
      if (event && event.currentTarget) {
        event.currentTarget.classList.add('situation-intro--exiting');
        forceAnimationExit(event.currentTarget);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setTypedCount(titleLength);
      setIsExiting(true);
    }

    function suppressCurrentPressClick() {
      completedOnPressRef.current = true;
      if (completedOnPressTimerRef.current) clearTimeout(completedOnPressTimerRef.current);
      completedOnPressTimerRef.current = window.setTimeout(function () {
        completedOnPressRef.current = false;
        completedOnPressTimerRef.current = null;
      }, 450);
    }

    function stopPressEvent(event) {
      stopTapDemoEvent(event);
    }

    function advanceNow(event) {
      freezeTyping(event);
      if (advancingRef.current) return;
      advancingRef.current = true;
      haptic('advance', { key: 'situation-advance' });
      props.onContinue();
    }

    function handlePress(event) {
      if (advancingRef.current) {
        stopPressEvent(event);
        return;
      }

      if (!claimTapDemoPress(event)) return;

      if (!isComplete) {
        freezeTyping(event);
        suppressCurrentPressClick();
        return;
      }

      advanceNow(event);
    }

    function handleClick(event) {
      if (completedOnPressRef.current || advancingRef.current) {
        stopPressEvent(event);
        completedOnPressRef.current = false;
        return;
      }

      if (!claimTapDemoPress(event)) return;

      if (!isComplete) {
        freezeTyping(event);
        suppressCurrentPressClick();
        return;
      }

      advanceNow(event);
    }

    return jsx('button', {
      type: 'button',
      className: 'screen situation-intro'
        + (i.eyebrow ? ' situation-intro--series' : '')
        + (i.visual === 'sunrise' ? ' situation-intro--sunrise' : '')
        + (isExiting ? ' situation-intro--exiting' : ''),
      onClick: handleClick,
      onMouseDownCapture: handlePress,
      onPointerDownCapture: handlePress,
      onTouchStartCapture: handlePress,
      'aria-label': titleText + (isComplete ? ' tap to continue' : ''),
      children: [
        i.visual === 'sunrise' ? jsx('div', {
          className: 'sunrise-scene',
          'aria-hidden': true,
          children: [
            jsx('span', { className: 'sunrise-scene__ray sunrise-scene__ray--one' }),
            jsx('span', { className: 'sunrise-scene__ray sunrise-scene__ray--two' }),
            jsx('span', { className: 'sunrise-scene__ray sunrise-scene__ray--three' }),
            jsx('span', { className: 'sunrise-scene__sun' })
          ]
        }) : null,
        i.eyebrow ? jsx('div', {
          className: 'situation-intro__eyebrow',
          children: i.eyebrow
        }) : null,
        jsx('div', {
          className: 'situation-intro__copy',
          children: jsx('h1', {
            className: 'situation-intro__title',
            'aria-live': 'polite',
            children: renderTypedSegments(titleSegments, displayCount)
          })
        }),
        jsx('div', {
          className: 'situation-intro__prompt' + (isComplete ? ' is-visible' : ''),
          'aria-hidden': !isComplete,
          children: 'tap to continue'
        })
      ]
    });
  }

  function AnswerStatInterstitial(props) {
    var i = props.interstitial;
    var q = window.QUIZ && window.QUIZ.questionById ? window.QUIZ.questionById[i.answerFrom] : null;
    var selectedIdx = props.answers && typeof props.answers[i.answerFrom] === 'number'
      ? props.answers[i.answerFrom]
      : null;
    var selectedOption = q && selectedIdx != null ? q.options[selectedIdx] : null;
    var stat = selectedIdx != null && i.stats ? i.stats[selectedIdx] : null;
    var answerLabel = (stat && stat.answerLabel) || (selectedOption && selectedOption.label);
    function snoozeAnswerText(label) {
      if (!label) return '';
      if (label === '1') return '1 time';
      return label + ' times';
    }
    var snoozeAnswer = snoozeAnswerText(answerLabel);
    var bodyChildren;
    if (stat && answerLabel && i.answerFrom === 'snooze_count') {
      // Force a line break after "they" so the answer ("snooze 2 times") sits
      // on its own line and visually echoes the You said pill.
      bodyChildren = [
        'of people said they',
        jsx('br', { key: 'br' }),
        'snooze ' + snoozeAnswer
      ];
    } else if (stat && answerLabel) {
      bodyChildren = 'of people put the same answer: "' + answerLabel + '".';
    } else {
      bodyChildren = (i.fallback && i.fallback.body) || 'of people are dealing with the same thing.';
    }

    return jsx('div', {
      className: 'screen answer-stat-screen',
      children: [
        jsx('div', {
          className: 'answer-stat__content',
          children: [
            // "You said" pill anchors the user's own answer above everything
            // else on the screen — even the title — so the data that follows
            // feels personal from the first glance.
            snoozeAnswer
              ? jsx('p', {
                  className: 'answer-stat__you-said',
                  children: [
                    jsx('span', { children: 'You said:' }),
                    jsx('strong', { children: snoozeAnswer })
                  ]
                })
              : null,
            jsx('h2', { className: 'answer-stat__title', children: i.title }),
            jsx('div', {
              className: 'answer-stat__panel',
              children: [
                jsx('span', { className: 'answer-stat__percent', children: (stat && stat.percent) || (i.fallback && i.fallback.percent) || '66%' }),
                jsx('p', { className: 'answer-stat__body', children: bodyChildren })
              ]
            })
          ]
        }),
        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            className: 'btn btn--primary btn--light',
            onClick: function () { haptic("advance"); props.onContinue(); },
            children: i.cta || 'Continue'
          })
        })
      ]
    });
  }

  // ------------------------------------------------------------
  // GraphInterstitial — title + an exported Figma graph card image +
  // optional footer lines and/or benefit rows. Used by the v1.1
  // "You're not lazy" sleep-cycle graph and the "This is your
  // turnaround" graph. The graph itself is a pixel-exact Figma export
  // (@3x) so intricate vector curves render 1:1; the surrounding text
  // is real, crisp copy.
  // ------------------------------------------------------------
  function GraphInterstitial(props) {
    var i = props.interstitial;
    var QA = window.QA || {};
    var src = QA[i.image] || ('../assets/' + i.image);
    var titleLines = (i.title || '').split('\n');
    var titleSegments = getGraphTitleSegments(i, props.answers || {});
    useEffect(function () {
      if (!i.graph && i.id !== 'inter_days_gone') return undefined;
      haptic('selection', { key: 'graph-start-' + i.id, minInterval: 250 });
      var midpoint = window.setTimeout(function () {
        haptic('milestone', { key: 'graph-mid-' + i.id, minInterval: 250 });
      }, 760);
      var endpoint = window.setTimeout(function () {
        haptic('success', { key: 'graph-end-' + i.id, minInterval: 320 });
      }, 1450);
      return function () {
        clearTimeout(midpoint);
        clearTimeout(endpoint);
      };
    }, [i.id, i.graph]);

    return jsx('div', {
      className: 'screen graph-screen graph-screen--' + i.id,
      children: [
        i.id === 'inter_days_gone' ? null : jsx('div', {
          className: 'graph__header',
          children: [
            jsx('h2', {
              className: 'interstitial__title',
              children: titleSegments
                ? titleSegments.map(function (s, idx) {
                    return s.br
                      ? jsx('br', { key: 's' + idx })
                      : jsx('span', { key: 's' + idx, className: s.accent ? 'interstitial__title-accent' : undefined, children: s.t });
                  })
                : titleLines.map(function (t, idx) {
                    return idx === 0
                      ? jsx('span', { key: 't' + idx, children: t })
                      : [jsx('br', { key: 'br' + idx }), jsx('span', { key: 't' + idx, children: t })];
                  })
            }),
            i.subtitle ? jsx('p', { className: 'graph__subtitle', children: i.subtitle }) : null
          ]
        }),

        i.id === 'inter_days_gone' ? jsx(DaysGoneStark, { answers: props.answers || {} })
          : i.graph === 'sleepCycle' ? jsx(SleepCycleGraph, {})
          : i.graph === 'turnaround' ? jsx(TurnaroundGraph, {})
          : i.graph === 'potential' ? jsx(PotentialEnergyGraph, {})
          : jsx('img', { className: 'graph__image' + (i.narrowImage ? ' graph__image--narrow' : ''), src: src, alt: '' }),

        i.footer ? jsx('div', {
          className: 'graph__footer',
          children: i.footer.map(function (f, idx) {
            var content = f.parts ? renderSegments(toSegments(f.parts)) : f.text;
            return jsx('p', {
              key: 'f' + idx,
              className: 'graph__footer-line' + (f.dim ? ' graph__footer-line--dim' : ''),
              style: {
                '--footer-index': idx,
                '--footer-delay': (950 + idx * 520) + 'ms'
              },
              children: content
            });
          })
        }) : null,

        i.rowsImage ? jsx('img', {
          className: 'graph__rows-image',
          src: (QA[i.rowsImage] || ('../assets/' + i.rowsImage)),
          alt: ''
        }) : null,

        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            className: 'btn btn--primary btn--light',
            onClick: function () { haptic("advance"); props.onContinue(); },
            children: i.cta || 'Continue'
          })
        })
      ]
    });
  }
  window.GraphInterstitial = GraphInterstitial;

  function getPhoneLoss(answers) {
    var minutes = typeof answers.phone_time === 'number' ? answers.phone_time : 15;
    var annualHours = Math.round((minutes * 365) / 60);
    return {
      minutes: minutes,
      hours: annualHours,
      hoursLabel: annualHours + (minutes >= 60 ? '+' : '')
    };
  }

  function getGraphTitleSegments(interstitial, answers) {
    return interstitial.titleSegments;
  }

  function DaysGoneStark(props) {
    var loss = getPhoneLoss(props.answers || {});
    var minutesLabel = loss.minutes >= 60 ? '60+ minutes' : loss.minutes + ' minutes';
    var displayedHours = window.useCountUp
      ? window.useCountUp(loss.hours, { from: 0, duration: 980, delay: 430 })
      : loss.hours;
    var hoursLabel = Math.round(displayedHours) + (loss.minutes >= 60 ? '+' : '');
    var bookCount = Math.max(1, Math.round(loss.hours / 6));
    var workoutCount = Math.max(1, Math.round(loss.hours / 0.75));
    var podcastCount = Math.max(1, Math.round(loss.hours));
    return jsx('div', {
      className: 'days-gone-stark',
      children: [
        jsx('p', {
          className: 'days-gone-stark__line days-gone-stark__line--lead',
          children: [
            jsx('span', { children: minutesLabel }),
            ' every morning is'
          ]
        }),
        jsx('div', {
          className: 'days-gone-stark__hours',
          children: [
            jsx('span', { className: 'days-gone-stark__number', children: hoursLabel }),
            jsx('span', { className: 'days-gone-stark__unit', children: 'hours' })
          ]
        }),
        jsx('p', {
          className: 'days-gone-stark__line days-gone-stark__line--year',
          children: 'wasted in a year.'
        }),
        jsx('ul', {
          className: 'days-gone-stark__examples',
          children: [
            jsx('li', {
              className: 'days-gone-stark__examples-lead',
              children: 'In that time you could have'
            }),
            jsx('li', {
              children: [
                jsx('span', { className: 'days-gone-stark__icon', 'aria-hidden': true, children: '📚' }),
                'Read about ' + bookCount + ' books'
              ]
            }),
            jsx('li', {
              children: [
                jsx('span', { className: 'days-gone-stark__icon', 'aria-hidden': true, children: '🎧' }),
                'Listened to ' + podcastCount + ' podcasts'
              ]
            }),
            jsx('li', {
              children: [
                jsx('span', { className: 'days-gone-stark__icon', 'aria-hidden': true, children: '💪' }),
                'Completed ' + workoutCount + ' workouts'
              ]
            })
          ]
        })
      ]
    });
  }

  // ------------------------------------------------------------
  // SleepCycleGraph — "You're not lazy" graph. Uses the EXACT Figma
  // vector paths (node 11907:9843) so the curve matches 1:1, animated:
  // the gray curve draws on, the orange "snooze" dip draws over it, the
  // dashed climb + markers + dots + labels stagger in. viewBox 361×257.
  // ------------------------------------------------------------
  function SleepCycleGraph() {
    return jsx('div', {
      className: 'graph__card graph__card--cycle',
      children: jsx('svg', {
        className: 'graph__svg', viewBox: '0 0 361 257',
        xmlns: 'http://www.w3.org/2000/svg', 'aria-hidden': true,
        children: [
          // vertical dashed markers (Figma lines)
          jsx('line', { key: 'm1', className: 'gx-line gx-line--accent', x1: 144.822, y1: 54, x2: 144.822, y2: 198, style: { animationDelay: '650ms' } }),
          jsx('line', { key: 'm2', className: 'gx-line gx-line--light', x1: 255.822, y1: 54, x2: 255.822, y2: 198, style: { animationDelay: '1150ms' } }),
          // gray full curve (exact Figma path)
          jsx('path', { key: 'gray', className: 'gx-draw', d: 'M55.748 198.819L105.601 119.731C114.586 105.476 135.077 104.676 145.147 118.186L186.735 173.982C196.111 186.56 214.836 186.909 224.673 174.689L254.748 137.328', stroke: '#fff', strokeOpacity: 0.42, strokeWidth: 4, pathLength: 1, style: { animationDelay: '150ms' } }),
          // orange snooze dip overlay (exact Figma path)
          jsx('path', { key: 'orange', className: 'gx-draw', d: 'M148.348 122.471L186.72 173.999C196.098 186.593 214.844 186.938 224.678 174.697L256.749 134.781', stroke: '#ec6324', strokeWidth: 4, pathLength: 1, style: { animationDelay: '600ms' } }),
          // dashed climb to awake
          jsx('path', { key: 'dash', className: 'gx-dashfade', d: 'M250.248 142.847L300.747 80.8086', stroke: '#fff', strokeOpacity: 0.4, strokeWidth: 2, strokeDasharray: '6 3', strokeLinecap: 'round', style: { animationDelay: '1000ms' } }),
          // dots (Figma circles)
          jsx('circle', { key: 'd1', className: 'gx-dot gx-dot--accent', cx: 144.822, cy: 119, r: 6, style: { animationDelay: '650ms' } }),
          jsx('circle', { key: 'd2', className: 'gx-dot gx-dot--accent', cx: 207, cy: 183, r: 6, style: { animationDelay: '900ms' } }),
          jsx('circle', { key: 'd3', className: 'gx-dot gx-dot--light', cx: 254.822, cy: 137, r: 6, style: { animationDelay: '1150ms' } }),
          // labels
          jsx('text', { key: 'l1', className: 'gx-label gx-label--accent', x: 144.822, y: 44, style: { animationDelay: '700ms' }, children: 'alarm rings' }),
          jsx('text', { key: 'l2', className: 'gx-label gx-label--light', x: 255.822, y: 44, style: { animationDelay: '1200ms' }, children: 'snooze ends' }),
          jsx('text', { key: 'l3', className: 'gx-label gx-label--accent', x: 207, y: 208, style: { animationDelay: '950ms' }, children: 'Deep Sleep' }),
          // x-axis labels
          jsx('text', { key: 'x1', className: 'gx-axis', x: 78, y: 236, style: { animationDelay: '250ms' }, children: 'PRE ALARM' }),
          jsx('text', { key: 'x2', className: 'gx-axis gx-axis--accent', x: 200, y: 236, style: { animationDelay: '650ms' }, children: 'SNOOZE' }),
          jsx('text', { key: 'x3', className: 'gx-axis', x: 308, y: 236, style: { animationDelay: '1150ms' }, children: 'AWAKE' })
        ]
      })
    });
  }
  window.SleepCycleGraph = SleepCycleGraph;

  // ------------------------------------------------------------
  // TurnaroundGraph — "This is your turnaround" graph. Exact Figma
  // vectors (node 11901:9219): faint grid + ascending orange curve from
  // 10% to 100%. The curve draws on, then the end dot + 100% pill pop.
  // ------------------------------------------------------------
  function TurnaroundGraph() {
    var vx = [37.252, 85, 132.748, 180.5, 228.252, 276, 323.748];
    var hy = [226.836, 177.127, 127.418, 77.71, 28];
    var grid = vx.map(function (x, i) { return jsx('line', { key: 'v' + i, x1: x, y1: 6, x2: x, y2: 240 }); })
      .concat(hy.map(function (y, i) { return jsx('line', { key: 'h' + i, x1: 8, y1: y, x2: 353, y2: y }); }));
    return jsx('div', {
      className: 'graph__card graph__card--turn',
      children: jsx('svg', {
        className: 'graph__svg', viewBox: '0 0 361 257',
        xmlns: 'http://www.w3.org/2000/svg', 'aria-hidden': true,
        children: [
          jsx('g', { key: 'grid', className: 'gx-grid', children: grid }),
          // ascending curve (exact Figma path)
          jsx('path', { key: 'curve', className: 'gx-draw', d: 'M35.1895 201.276C114.906 201.276 245.185 142.059 323.079 35.0635', stroke: '#ec6324', strokeWidth: 3.6, pathLength: 1, style: { animationDelay: '250ms', animationDuration: '950ms' } }),
          // dots
          jsx('circle', { key: 'd0', className: 'gx-dot gx-dot--light', cx: 37, cy: 200.459, r: 5, style: { animationDelay: '300ms' } }),
          jsx('circle', { key: 'd1', className: 'gx-dot gx-dot--accent', cx: 323, cy: 34.837, r: 6, style: { animationDelay: '1200ms' } }),
          // 10% pill
          jsx('g', { key: 'p0', className: 'gx-pill', style: { animationDelay: '450ms' }, children: [
            jsx('rect', { key: 'r', x: 32, y: 163.837, width: 39, height: 20, rx: 10, className: 'gx-pill__bg' }),
            jsx('text', { key: 't', x: 51.5, y: 177.5, className: 'gx-pill__txt', children: '10%' })
          ] }),
          // 100% pill (orange border)
          jsx('g', { key: 'p1', className: 'gx-pill', style: { animationDelay: '1250ms' }, children: [
            jsx('rect', { key: 'r', x: 259.456, y: 24.456, width: 46.089, height: 19.089, rx: 9.544, className: 'gx-pill__bg gx-pill__bg--accent' }),
            jsx('text', { key: 't', x: 282.5, y: 37.5, className: 'gx-pill__txt gx-pill__txt--accent', children: '100%' })
          ] }),
          // axis labels
          jsx('text', { key: 'x0', className: 'gx-axis gx-axis--name', x: 72, y: 250, style: { animationDelay: '500ms' }, children: 'You right now' }),
          jsx('text', { key: 'x1', className: 'gx-axis gx-axis--name gx-axis--accent', x: 296, y: 250, style: { animationDelay: '1300ms' }, children: 'You with Flow' })
        ]
      })
    });
  }
  window.TurnaroundGraph = TurnaroundGraph;

  function PotentialEnergyGraph() {
    var vx = [37.252, 85, 132.748, 180.5, 228.252, 276, 323.748];
    var hy = [226.836, 177.127, 127.418, 77.71, 28];
    var grid = vx.map(function (x, i) { return jsx('line', { key: 'v' + i, x1: x, y1: 6, x2: x, y2: 240 }); })
      .concat(hy.map(function (y, i) { return jsx('line', { key: 'h' + i, x1: 8, y1: y, x2: 353, y2: y }); }));
    return jsx('div', {
      className: 'graph__card graph__card--potential',
      children: jsx('svg', {
        className: 'graph__svg', viewBox: '0 0 361 257',
        xmlns: 'http://www.w3.org/2000/svg', 'aria-hidden': true,
        children: [
          jsx('g', { key: 'grid', className: 'gx-grid', children: grid }),
          jsx('path', { key: 'low', className: 'gx-draw potential-line potential-line--drag', d: 'M35 190C76 184 110 181 145 184C180 187 209 199 242 195C276 191 299 178 324 169', stroke: 'rgba(255,255,255,0.32)', strokeWidth: 3.2, pathLength: 1, style: { animationDelay: '150ms', animationDuration: '720ms' } }),
          jsx('path', { key: 'glow', className: 'gx-draw potential-line potential-line--glow', d: 'M151 141C182 112 203 91 232 76C260 61 291 52 325 33', stroke: 'rgba(236,99,36,0.35)', strokeWidth: 12, pathLength: 1, style: { animationDelay: '760ms', animationDuration: '860ms' } }),
          jsx('path', { key: 'energy', className: 'gx-draw potential-line potential-line--energy', d: 'M35 198C88 192 120 170 151 141C182 112 203 91 232 76C260 61 291 52 325 33', stroke: '#ec6324', strokeWidth: 4, pathLength: 1, style: { animationDelay: '620ms', animationDuration: '980ms' } }),
          jsx('circle', { key: 'd0', className: 'gx-dot gx-dot--light', cx: 35, cy: 198, r: 5, style: { animationDelay: '230ms' } }),
          jsx('circle', { key: 'd1', className: 'gx-dot gx-dot--accent', cx: 325, cy: 33, r: 7, style: { animationDelay: '1560ms' } }),
          jsx('g', { key: 'p0', className: 'gx-pill', style: { animationDelay: '420ms' }, children: [
            jsx('rect', { key: 'r', x: 25, y: 155, width: 76, height: 22, rx: 11, className: 'gx-pill__bg' }),
            jsx('text', { key: 't', x: 63, y: 170, className: 'gx-pill__txt', children: 'drained' })
          ] }),
          jsx('g', { key: 'p1', className: 'gx-pill', style: { animationDelay: '1620ms' }, children: [
            jsx('rect', { key: 'r', x: 240, y: 20, width: 82, height: 23, rx: 11.5, className: 'gx-pill__bg gx-pill__bg--accent' }),
            jsx('text', { key: 't', x: 281, y: 35.5, className: 'gx-pill__txt gx-pill__txt--accent', children: 'potential' })
          ] }),
          jsx('text', { key: 'y0', className: 'gx-label gx-label--light potential-label potential-label--energy', x: 51, y: 30, style: { animationDelay: '1120ms' }, children: 'energy' }),
          jsx('text', { key: 'x0', className: 'gx-axis gx-axis--name', x: 65, y: 250, style: { animationDelay: '460ms' }, children: 'Today' }),
          jsx('text', { key: 'x1', className: 'gx-axis gx-axis--name gx-axis--accent', x: 292, y: 250, style: { animationDelay: '1580ms' }, children: 'With Flow' })
        ]
      })
    });
  }
  window.PotentialEnergyGraph = PotentialEnergyGraph;

  // ------------------------------------------------------------
  // SnoozeBarsInterstitial — Figma v final (11888:8476). Gradient card
  // with three horizontal bars (Week / Month / Year). Week + Month are
  // red-tinted fills that grow; Year is a full solid-red bar. Each bar's
  // value sits right-aligned. Red-accent footer line below.
  // ------------------------------------------------------------
  function SnoozeBarsInterstitial(props) {
    var i = props.interstitial;
    var bars = i.bars || [];
    return jsx('div', {
      className: 'screen barchart-screen',
      children: [
        jsx('div', {
          className: 'barchart__header',
          children: [
            jsx('h2', { className: 'interstitial__title', children: i.title }),
            (i.subtitlePre || i.subtitleEm) ? jsx('p', {
              className: 'barchart__subtitle',
              children: [
                i.subtitlePre,
                jsx('span', { key: 'em', className: 'barchart__subtitle-em', children: i.subtitleEm }),
                i.subtitlePost
              ]
            }) : null
          ]
        }),

        jsx('div', {
          className: 'barchart__card',
          children: [
            jsx('div', {
              className: 'barchart__bars',
              children: bars.map(function (b, idx) {
                return jsx('div', {
                  key: 'b' + idx,
                  className: 'barchart__row',
                  children: [
                    jsx('span', { className: 'barchart__label', children: b.label }),
                    jsx('div', {
                      className: 'barchart__track' + (b.full ? ' barchart__track--full' : ''),
                      children: [
                        b.full ? null : jsx('div', {
                          key: 'fill',
                          className: 'barchart__fill',
                          style: { width: b.fill + '%', background: b.fillColor, animationDelay: (idx * 160) + 'ms' }
                        }),
                        jsx('div', { key: 'val', className: 'barchart__value', children: b.value })
                      ]
                    })
                  ]
                });
              })
            }),

            (i.footerPre || i.footerLine2) ? jsx('p', {
              className: 'barchart__footer',
              children: [
                i.footerPre,
                jsx('span', { key: 'em', className: 'barchart__footer-em', children: i.footerEm }),
                i.footerPost,
                jsx('br', { key: 'br' }),
                i.footerLine2
              ]
            }) : null
          ]
        }),

        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            className: 'btn btn--primary btn--light',
            onClick: function () { haptic("advance"); props.onContinue(); },
            children: i.cta || 'Continue'
          })
        })
      ]
    });
  }
  window.SnoozeBarsInterstitial = SnoozeBarsInterstitial;

  // ------------------------------------------------------------
  // StatShockInterstitial
  // Three stat cards stagger in (340ms / 940ms / 1620ms via CSS),
  // each with its number counting up from 0. CSS handles the row
  // appearance + sweep; useCountUp does the digit animation.
  // ------------------------------------------------------------
  function StatShockInterstitial(props) {
    var i = props.interstitial;
    var stats = i.stats || [];
    return jsx('div', {
      className: 'screen stat-shock',
      children: [
        i.kicker ? jsx('div', { className: 'stat-shock__kicker', children: i.kicker }) : null,
        jsx('h2', { className: 'stat-shock__title', children: i.title }),

        jsx('div', {
          className: 'stat-grid',
          children: stats.map(function (s, idx) {
            return jsx(StatCard, {
              key: String(idx),
              index: idx,
              label: s.label,
              from: s.from || 0,
              to: s.value,
              unit: s.unit,
              decimals: s.decimals || 0,
              tone: s.tone
            });
          })
        }),

        i.body ? jsx('p', { className: 'stat-shock__body', children: i.body }) : null,

        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            className: 'btn btn--primary btn--light',
            onClick: function () { haptic("advance"); props.onContinue(); },
            children: i.cta || 'Continue'
          })
        })
      ]
    });
  }

  function StatCard(props) {
    var cardDelays = [340, 940, 1620];
    var startDelay = (cardDelays[props.index] || 0) + 200;
    var count = window.useCountUp(props.to, {
      from: props.from || 0,
      duration: 900,
      delay: startDelay,
      decimals: props.decimals || 0
    });
    var formatted = (props.decimals && props.decimals > 0)
      ? count.toFixed(props.decimals)
      : Math.round(count).toLocaleString();
    var toneClass = props.tone ? ' stat-card--' + props.tone : '';
    return jsx('div', {
      className: 'stat-card stat-card--' + (props.index + 1) + toneClass,
      children: [
        jsx('span', { className: 'stat-card__label', children: props.label }),
        jsx('span', {
          className: 'stat-card__value',
          children: formatted + ' ' + props.unit
        })
      ]
    });
  }

  // ------------------------------------------------------------
  // ReframeInterstitial
  // Three rows fade in, then strike through one by one,
  // then the "It's not willpower. It's design." line punches in.
  // ------------------------------------------------------------
  function ReframeInterstitial(props) {
    var i = props.interstitial;
    var rows = i.strikeRows || [];
    return jsx('div', {
      className: 'screen reframe',
      children: [
        i.kicker ? jsx('div', { className: 'reframe__kicker', children: i.kicker }) : null,

        jsx('ul', {
          className: 'reframe__list',
          children: rows.map(function (text, idx) {
            return jsx('li', {
              key: String(idx),
              className: 'reframe-row reframe-row--' + (idx + 1),
              children: [
                jsx('span', { className: 'reframe-row__check' }),
                jsx('span', { className: 'reframe-row__text', children: text })
              ]
            });
          })
        }),

        jsx('h2', {
          className: 'reframe__punch',
          children: [
            i.punchLeft || "It's not ",
            jsx('em', { children: i.punchAccent || 'willpower' }),
            i.punchRight || ". It's ",
            jsx('em', { children: i.punchAccent2 || 'design' }),
            i.punchEnd || '.'
          ]
        }),

        i.body ? jsx('p', { className: 'reframe__body', children: i.body }) : null,

        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            className: 'btn btn--primary btn--light',
            onClick: function () { haptic("advance"); props.onContinue(); },
            children: i.cta || 'Continue'
          })
        })
      ]
    });
  }

  // ------------------------------------------------------------
  // TimelineInterstitial — product-proof layout
  // Each row: timestamp pill on the left, action text on the right,
  // connected by an accent-color vertical track.
  // ------------------------------------------------------------
  function TimelineInterstitial(props) {
    var i = props.interstitial;
    return jsx('div', {
      className: 'screen timeline-screen',
      children: [
        i.kicker ? jsx('div', { className: 'interstitial__kicker', children: i.kicker }) : null,
        jsx('h2', { className: 'interstitial__title', children: i.title }),

        jsx('ol', {
          className: 'timeline',
          children: (i.timeline || []).map(function (row, idx) {
            return jsx('li', {
              key: String(idx),
              className: 'timeline__row',
              children: [
                jsx('div', { className: 'timeline__time', children: row.time }),
                jsx('div', { className: 'timeline__track' }),
                jsx('div', { className: 'timeline__text', children: row.text })
              ]
            });
          })
        }),

        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            className: 'btn btn--primary btn--light',
            onClick: function () { haptic("advance"); props.onContinue(); },
            children: i.cta || 'Continue'
          })
        })
      ]
    });
  }

  // ------------------------------------------------------------
  // AppBreakdownInterstitial — "That's X days a year. Gone."
  // Animated component (NOT an image). Each app row staggers in,
  // its minute value counts up, then the total card appears with
  // its own count-up. Matches the stat-shock visual rhythm.
  // ------------------------------------------------------------
  function AppBreakdownInterstitial(props) {
    var i = props.interstitial;
    var apps  = i.apps || [];
    var rowDelays = apps.map(function (_, idx) { return 320 + idx * 220; });
    var totalDelay = rowDelays.length ? rowDelays[rowDelays.length - 1] + 600 : 800;

    return jsx('div', {
      className: 'screen app-breakdown',
      children: [
        jsx('h2', { className: 'app-breakdown__title', children: i.title }),
        i.body ? jsx('p', { className: 'app-breakdown__body', children: i.body }) : null,

        jsx('div', {
          className: 'app-breakdown__rows',
          children: apps.map(function (a, idx) {
            return jsx(AppRow, {
              key: 'a' + idx,
              index: idx,
              app: a,
              entryDelay: rowDelays[idx],
              countDelay: rowDelays[idx] + 220
            });
          })
        }),

        jsx(AppTotalCard, {
          label: i.totalLabel || 'Adds up to',
          value: i.totalValue,
          decimals: i.totalDecimals || 0,
          unit: i.totalUnit || '',
          entryDelay: totalDelay,
          countDelay: totalDelay + 200
        }),

        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            className: 'btn btn--primary btn--light',
            onClick: function () { haptic("advance"); props.onContinue(); },
            children: i.cta || 'Continue'
          })
        })
      ]
    });
  }

  function AppRow(props) {
    var a = props.app;
    var count = window.useCountUp(a.value, {
      from: 0, duration: 760, delay: props.countDelay, decimals: 0
    });
    return jsx('div', {
      className: 'app-row',
      style: { animationDelay: props.entryDelay + 'ms' },
      children: [
        jsx('span', { className: 'app-row__name', children: a.name }),
        jsx('span', {
          className: 'app-row__value',
          children: count.toLocaleString() + ' ' + a.unit
        })
      ]
    });
  }

  function AppTotalCard(props) {
    var count = window.useCountUp(props.value, {
      from: 0, duration: 900, delay: props.countDelay, decimals: props.decimals || 0
    });
    var formatted = (props.decimals && props.decimals > 0)
      ? count.toFixed(props.decimals)
      : Math.round(count).toLocaleString();
    return jsx('div', {
      className: 'app-total',
      style: { animationDelay: props.entryDelay + 'ms' },
      children: [
        jsx('span', { className: 'app-total__label', children: props.label }),
        jsx('span', {
          className: 'app-total__value',
          children: [
            formatted + ' ',
            jsx('span', { className: 'app-total__unit', children: props.unit })
          ]
        })
      ]
    });
  }

  // ------------------------------------------------------------
  // ImageInterstitial — renders a Figma-exported image as the
  // content of the interstitial. Used when the visual fidelity of
  // the Figma frame matters more than CSS recreation (apps
  // breakdown, you-vs-flow, morning-with-flow).
  // ------------------------------------------------------------
  function ImageInterstitial(props) {
    var i = props.interstitial;
    var QA = window.QA || {};
    var src = (QA[i.image] || ('../assets/' + i.image));
    return jsx('div', {
      className: 'screen image-interstitial' + (i.className ? ' ' + i.className : ''),
      children: [
        i.title ? jsx('h2', { className: 'interstitial__title', children: i.title }) : null,
        i.subtitle ? jsx('p', { className: 'image-interstitial__subtitle', children: i.subtitle }) : null,
        jsx('img', {
          src: src,
          alt: '',
          'aria-hidden': true,
          className: 'image-interstitial__img'
        }),
        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            className: 'btn btn--primary btn--light',
            onClick: function () { haptic("advance"); props.onContinue(); },
            children: i.cta || 'Continue'
          })
        })
      ]
    });
  }

  // ------------------------------------------------------------
  // CycleInterstitial — "It's a cycle." circular diagram.
  // 4 outer nodes (Bad night / Bad sleep / Bad morning / Bad day)
  // arranged around a center orange "Flow fixes it" node, connected
  // by an SVG circle that suggests the loop.
  // ------------------------------------------------------------
  function CycleInterstitial(props) {
    var i = props.interstitial;
    var nodes = i.nodes || [];
    // Figma v1.1 (11909:9941): ring of 4 black circles + orange center.
    //   - Top ("Poor sleep") and right ("Snooze 5x") have an orange border
    //   - The N→E arc between them is a dashed orange arrow; other 3 arcs
    //     are subtle gray with arrowheads
    //   - Center ("You can break it.") is the orange node
    var centerWords = (i.center || '').split(' ');
    var centerMid = Math.ceil(centerWords.length / 2);
    var centerLine1 = centerWords.slice(0, centerMid).join(' ');
    var centerLine2 = centerWords.slice(centerMid).join(' ');
    return jsx('div', {
      className: 'screen cycle-screen',
      children: [
        jsx('div', {
          className: 'cycle__header',
          children: [
            jsx('h2', { className: 'interstitial__title', children: i.title }),
            i.body ? jsx('p', { className: 'cycle__subtitle', children: i.body }) : null
          ]
        }),

        jsx('div', {
          className: 'cycle',
          children: jsx('svg', {
            className: 'cycle__svg',
            viewBox: '0 0 320 320',
            xmlns: 'http://www.w3.org/2000/svg',
            'aria-hidden': true,
            children: [
              jsx('defs', {
                children: [
                  jsx('linearGradient', {
                    key: 'center-grad',
                    id: 'cycle-center-grad',
                    x1: '0%',
                    y1: '0%',
                    x2: '100%',
                    y2: '100%',
                    children: [
                      jsx('stop', { offset: '0%', stopColor: '#ff7a33' }),
                      jsx('stop', { offset: '58%', stopColor: '#ec6324' }),
                      jsx('stop', { offset: '100%', stopColor: '#d94d18' })
                    ]
                  }),
                  jsx('linearGradient', {
                    key: 'node-fill',
                    id: 'cycle-node-fill',
                    x1: '0%',
                    y1: '0%',
                    x2: '100%',
                    y2: '100%',
                    children: [
                      jsx('stop', { offset: '0%', stopColor: '#202020' }),
                      jsx('stop', { offset: '100%', stopColor: '#0b0b0b' })
                    ]
                  }),
                  jsx('filter', {
                    key: 'glow',
                    id: 'cycle-glow',
                    x: '-50%',
                    y: '-50%',
                    width: '200%',
                    height: '200%',
                    children: [
                      jsx('feGaussianBlur', { stdDeviation: '5', result: 'blur' }),
                      jsx('feColorMatrix', {
                        in: 'blur',
                        type: 'matrix',
                        values: '1 0 0 0 0.93 0 1 0 0 0.39 0 0 1 0 0.14 0 0 0 0.75 0',
                        result: 'glow'
                      }),
                      jsx('feMerge', {
                        children: [
                          jsx('feMergeNode', { in: 'glow' }),
                          jsx('feMergeNode', { in: 'SourceGraphic' })
                        ]
                      })
                    ]
                  }),
                  jsx('marker', {
                    key: 'm-gray',
                    id: 'cycle-arrow',
                    viewBox: '0 0 12 12', refX: 9, refY: 6,
                    markerWidth: 8, markerHeight: 8,
                    orient: 'auto',
                    children: jsx('path', { d: 'M 1 1 L 11 6 L 1 11 z', fill: 'rgba(255,255,255,0.45)' })
                  }),
                  jsx('marker', {
                    key: 'm-accent',
                    id: 'cycle-arrow-accent',
                    viewBox: '0 0 12 12', refX: 9, refY: 6,
                    markerWidth: 8, markerHeight: 8,
                    orient: 'auto',
                    children: jsx('path', { d: 'M 1 1 L 11 6 L 1 11 z', fill: '#ec6324' })
                  }),
                ]
              }),

              jsx('path', { className: 'cycle__arc cycle__arc--accent', d: 'M 212.5 69.1  A 105 105 0 0 1 250.9 107.5', markerEnd: 'url(#cycle-arrow-accent)', style: { animationDelay: '520ms' } }),
              jsx('path', { className: 'cycle__arc cycle__arc--soft', d: 'M 250.9 212.5 A 105 105 0 0 1 212.5 250.9', markerEnd: 'url(#cycle-arrow)', style: { animationDelay: '760ms' } }),
              jsx('path', { className: 'cycle__arc cycle__arc--soft', d: 'M 107.5 250.9 A 105 105 0 0 1 69.1  212.5', markerEnd: 'url(#cycle-arrow)', style: { animationDelay: '1000ms' } }),
              jsx('path', { className: 'cycle__arc cycle__arc--soft', d: 'M 69.1  107.5 A 105 105 0 0 1 107.5 69.1', markerEnd: 'url(#cycle-arrow)', style: { animationDelay: '1240ms' } }),

              renderCycleNode(160,  55, nodes[0] && nodes[0].label, 0, true),
              renderCycleNode(265, 160, nodes[1] && nodes[1].label, 1, true),
              renderCycleNode(160, 265, nodes[2] && nodes[2].label, 2, false),
              renderCycleNode( 55, 160, nodes[3] && nodes[3].label, 3, false),

              jsx('g', {
                className: 'cycle__center-g',
                children: [
                  jsx('circle', {
                    cx: 160, cy: 160, r: 42,
                    className: 'cycle__center-circle'
                  }),
                  jsx('text', {
                    x: 160, y: centerLine2 ? 155 : 160, fill: '#fff',
                    textAnchor: 'middle',
                    dominantBaseline: 'middle',
                    fontFamily: 'Inter, "Inter Display", sans-serif',
                    fontSize: 12,
                    fontWeight: 600,
                    children: centerLine1
                  }),
                  centerLine2 ? jsx('text', {
                    x: 160, y: 171, fill: '#fff',
                    textAnchor: 'middle',
                    dominantBaseline: 'middle',
                    fontFamily: 'Inter, "Inter Display", sans-serif',
                    fontSize: 12,
                    fontWeight: 600,
                    children: centerLine2
                  }) : null
                ]
              })
            ]
          })
        }),

        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            className: 'btn btn--primary btn--light',
            onClick: function () { haptic("advance"); props.onContinue(); },
            children: i.cta || 'Continue'
          })
        })
      ]
    });
  }

  // Helper: render a circular node centered at (cx, cy).
  function renderCycleNode(cx, cy, label, idx, highlighted) {
    if (!label) return null;
    var parts = label.split(' ');
    var line1 = parts[0];
    var line2 = parts.slice(1).join(' ');
    return jsx('g', {
      key: 'n' + idx,
      className: 'cycle__node-g',
      style: { animationDelay: (80 + idx * 160) + 'ms' },
      children: [
        jsx('circle', {
          cx: cx, cy: cy, r: 42,
          className: 'cycle__node-shadow'
        }),
        jsx('circle', {
          cx: cx, cy: cy, r: 42,
          className: 'cycle__node-circle' + (highlighted ? ' cycle__node-circle--hot' : '')
        }),
        jsx('text', {
          x: cx, y: line2 ? cy - 5 : cy, fill: '#fff',
          textAnchor: 'middle',
          dominantBaseline: 'middle',
          fontFamily: 'Inter, "Inter Display", sans-serif',
          fontSize: 12,
          fontWeight: 600,
          children: line1
        }),
        line2 ? jsx('text', {
          x: cx, y: cy + 11,
          fill: 'rgba(255,255,255,0.4)',
          textAnchor: 'middle',
          dominantBaseline: 'middle',
          fontFamily: 'Inter, "Inter Display", sans-serif',
          fontSize: 12,
          fontWeight: 500,
          children: line2
        }) : null
      ]
    });
  }

  // ------------------------------------------------------------
  // ComparisonInterstitial — "You're not lazy" iOS vs Flow.
  // Two cards side-by-side: gray iOS-style alarm list (4 rows of
  // 6:30/6:35/6:40/6:45) vs single orange-tinted Flow alarm card
  // with mission. Title supports embedded \n for line break.
  // ------------------------------------------------------------
  function ComparisonInterstitial(props) {
    var i = props.interstitial;
    var titleParts = (i.title || '').split('\n');
    var QA = window.QA || {};
    var img = QA['ios-vs-flow-comparison.png'] || '../assets/ios-vs-flow-comparison.png';
    return jsx('div', {
      className: 'screen comparison-screen',
      children: [
        jsx('h2', {
          className: 'interstitial__title',
          children: titleParts.map(function (p, idx) {
            return idx === 0
              ? jsx('span', { key: 'p0', children: p })
              : [jsx('br', { key: 'br' + idx }), jsx('span', { key: 'p' + idx, children: p })];
          })
        }),
        i.body ? jsx('p', { className: 'comparison__body', children: i.body }) : null,

        // Pixel-perfect comparison rendered from the actual Figma export —
        // no more CSS approximation.
        jsx('img', {
          src: img,
          alt: 'iOS Alarms vs Flow Alarm',
          className: 'comparison__image'
        }),

        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            className: 'btn btn--primary btn--light',
            onClick: function () { haptic("advance"); props.onContinue(); },
            children: i.cta || 'Continue'
          })
        })
      ]
    });
  }

  // ------------------------------------------------------------
  // MorningWithFlowInterstitial — Figma 11821:14932
  // Single card with a vertical green-rail timeline. 4 rows of
  // (time + description). 24px green check dots + 2.4px green
  // connecting line between them. The rail and text are inside a
  // dark surface card.
  // ------------------------------------------------------------
  // Format minutes-from-midnight as "6:30 AM".
  function formatClock(min) {
    min = ((Math.round(min) % 1440) + 1440) % 1440;
    var h = Math.floor(min / 60), m = min % 60;
    var ampm = h < 12 ? 'AM' : 'PM';
    var hr = h % 12; if (hr === 0) hr = 12;
    return hr + ':' + (m < 10 ? '0' + m : m) + ' ' + ampm;
  }

  function resolveWakeBase(config, answers) {
    var base = config.baseDefault || 390;
    if (!config.baseFrom) return base;

    var q = window.QUIZ && window.QUIZ.questionById ? window.QUIZ.questionById[config.baseFrom] : null;
    var ansIdx = answers && answers[config.baseFrom];

    try {
      var stored = sessionStorage.getItem('flow-wake-time-answer');
      if (stored !== null && stored !== '') ansIdx = Number(stored);
    } catch (e) {}

    if (q && ansIdx != null && q.options[ansIdx] && typeof q.options[ansIdx].base === 'number') {
      base = q.options[ansIdx].base;
    }

    return base;
  }

  function formatDemoClock(min) {
    var clock = formatClock(min);
    return clock.replace(':00 ', ' ');
  }

  function forceAnimationExit(root) {
    if (!root || !root.querySelectorAll) return;
    var animations = [];
    try {
      animations = root.getAnimations ? root.getAnimations({ subtree: true }) : [];
      animations.forEach(function (animation) { animation.pause(); });
    } catch (e) {}

    var nodes = [root].concat(Array.prototype.slice.call(root.querySelectorAll('*')));
    nodes.forEach(function (el) {
      var computed = window.getComputedStyle(el);
      var position = computed.position;

      el.style.opacity = computed.opacity;
      el.style.transform = computed.transform === 'none' ? 'none' : computed.transform;
      el.style.filter = computed.filter === 'none' ? 'none' : computed.filter;
      el.style.animationPlayState = 'paused';
      el.style.animation = 'none';
      el.style.transition = 'none';

      if (position === 'absolute' || position === 'fixed') {
        if (computed.left !== 'auto') el.style.left = computed.left;
        if (computed.top !== 'auto') el.style.top = computed.top;
        if (computed.right !== 'auto') el.style.right = computed.right;
        if (computed.bottom !== 'auto') el.style.bottom = computed.bottom;
      }
    });

    try {
      animations.forEach(function (animation) { animation.cancel(); });
    } catch (e) {}

    // Force style recalculation before React swaps screens.
    root.getBoundingClientRect();
  }

  function FlowDemoAlarmInterstitial(props) {
    var i = props.interstitial;
    var QA = window.QA || {};
    var wakeBase = resolveWakeBase(i, props.answers || {});
    var wakeTime = formatDemoClock(wakeBase);
    var isWakeTimeDemo = !!i.baseFrom;
    var skipIntro = isWakeTimeDemo && props.navigationDirection === 'back';
    var forceSettledState = useState(skipIntro);
    var forceSettled = forceSettledState[0] || skipIntro;
    var setForceSettled = forceSettledState[1];
    var exitingState = useState(false);
    var isExiting = exitingState[0];
    var setIsExiting = exitingState[1];
    var shouldPlayIntro = isWakeTimeDemo && !skipIntro;
    var introVisibleState = useState(shouldPlayIntro);
    var introVisible = introVisibleState[0] && !forceSettled;
    var setIntroVisible = introVisibleState[1];
    var timeSettledState = useState(!shouldPlayIntro);
    var timeSettled = timeSettledState[0] || forceSettled || !isWakeTimeDemo;
    var setTimeSettled = timeSettledState[1];
    var screenRef = useRef(null);
    var timeRef = useRef(null);
    var introRef = useRef(null);
    var introAnimationRef = useRef(null);
    var advancingRef = useRef(false);
    var eyebrow = i.eyebrow || "HERE'S HOW FLOW WORKS";
    var titleSegments = toSegments(i.title || 'Your alarm rings.');
    var titleText = segmentsString(titleSegments);
    var ariaLabel = isWakeTimeDemo
      ? wakeTime + '. Your alarm rings. Tap to continue.'
      : titleText + (i.subtitle ? ' ' + i.subtitle : '') + ' Tap to continue.';
    // Look up imageMap by wake_time answer directly so non-wake-demo screens
    // (e.g. the dock-scan interstitial) can personalize without inheriting
    // the wake-time intro animation that baseFrom triggers.
    var imageMapKey = wakeBase;
    if (i.imageMap && !isWakeTimeDemo) {
      var wq = window.QUIZ && window.QUIZ.questionById ? window.QUIZ.questionById.wake_time : null;
      var ans = (props.answers || {}).wake_time;
      try {
        var stored = sessionStorage.getItem('flow-wake-time-answer');
        if (stored !== null && stored !== '') ans = Number(stored);
      } catch (e) {}
      if (wq && ans != null && wq.options[ans] && typeof wq.options[ans].base === 'number') {
        imageMapKey = wq.options[ans].base;
      } else {
        imageMapKey = null;
      }
    }
    var imageName = (i.imageMap && imageMapKey != null && i.imageMap[imageMapKey]) || i.image;
    var imageSrc = QA[imageName] || imageName;
    var localImageSrc = '../assets/' + imageName;
    if ((/^flow-(alarm-rings|dock-scan)-demo(-[0-9a-z]+)?\.png$/.test(imageName)) && imageSrc) {
      imageSrc += (imageSrc.indexOf('?') === -1 ? '?' : '&') + 'v=flow-demo-20260529-2213';
      localImageSrc += '?v=flow-demo-20260529-2213';
    }

    useEffect(function () {
      if (!introVisible) return undefined;

      var screenEl = screenRef.current;
      var finalEl = timeRef.current;
      var introEl = introRef.current;
      if (!screenEl || !finalEl || !introEl) return undefined;

      var prefersReducedMotion = false;
      try {
        prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      } catch (e) {}

      if (prefersReducedMotion || typeof introEl.animate !== 'function') {
        setTimeSettled(true);
        setIntroVisible(false);
        return undefined;
      }

      var raf = requestAnimationFrame(function () {
        var screenRect = screenEl.getBoundingClientRect();
        var finalRect = finalEl.getBoundingClientRect();
        var finalX = finalRect.left - screenRect.left + finalRect.width / 2;
        var finalY = finalRect.top - screenRect.top + finalRect.height / 2;
        var finalLeft = finalX + 'px';
        var finalTop = finalY + 'px';

        introEl.style.left = '50%';
        introEl.style.top = '50%';
        introEl.style.transform = 'translate(-50%, -50%) scale(2.14) rotate(0deg)';

        var animation = introEl.animate([
          { left: '50%', top: '50%', transform: 'translate(-50%, -50%) scale(2.14) rotate(0deg)', easing: 'ease-in-out' },
          { left: '50%', top: '50%', transform: 'translate(-50%, -50%) scale(2.15) rotate(-11deg)', offset: 0.075, easing: 'ease-in-out' },
          { left: '50%', top: '50%', transform: 'translate(-50%, -50%) scale(2.15) rotate(11deg)', offset: 0.15, easing: 'ease-in-out' },
          { left: '50%', top: '50%', transform: 'translate(-50%, -50%) scale(2.15) rotate(-10deg)', offset: 0.225, easing: 'ease-in-out' },
          { left: '50%', top: '50%', transform: 'translate(-50%, -50%) scale(2.15) rotate(10deg)', offset: 0.30, easing: 'ease-in-out' },
          { left: '50%', top: '50%', transform: 'translate(-50%, -50%) scale(2.15) rotate(-8deg)', offset: 0.36, easing: 'ease-in-out' },
          { left: '50%', top: '50%', transform: 'translate(-50%, -50%) scale(2.12) rotate(0deg)', offset: 0.405, easing: 'cubic-bezier(0.8, 0, 0.9, 0.55)' },
          { left: finalLeft, top: finalTop, transform: 'translate(-50%, -50%) scale(1.03) rotate(0deg)', offset: 0.84, easing: 'cubic-bezier(0.18, 0.75, 0.3, 1)' },
          { left: finalLeft, top: finalTop, transform: 'translate(-50%, -50%) scale(1) rotate(0deg)' }
        ], {
          duration: 2350,
          easing: 'linear',
          fill: 'forwards'
        });

        introAnimationRef.current = animation;
        haptic('motion', { key: 'flow-demo-motion', minInterval: 220 });
        animation.onfinish = function () {
          introEl.style.left = finalLeft;
          introEl.style.top = finalTop;
          introEl.style.transform = 'translate(-50%, -50%) scale(1) rotate(0deg)';
          introAnimationRef.current = null;
          haptic('milestone', { key: 'flow-demo-land', minInterval: 260 });
          setTimeSettled(true);
          window.setTimeout(function () { setIntroVisible(false); }, 80);
        };
      });

      return function () {
        cancelAnimationFrame(raf);
        if (introAnimationRef.current) {
          if (introRef.current) introRef.current.style.display = 'none';
          introAnimationRef.current.cancel();
          introAnimationRef.current = null;
        }
      };
    }, [introVisible]);

    function freezeIntro(event) {
      if (event && event.currentTarget) {
        event.currentTarget.classList.add('flow-demo-alarm--settled');
        event.currentTarget.classList.add('flow-demo-alarm--exiting');
        forceAnimationExit(event.currentTarget);
      }
      if (introRef.current) introRef.current.style.display = 'none';
      if (introAnimationRef.current) {
        introAnimationRef.current.cancel();
        introAnimationRef.current = null;
      }
      setIsExiting(true);
      if (isWakeTimeDemo) {
        setTimeSettled(true);
        setIntroVisible(false);
        setForceSettled(true);
      }
    }

    function stopPressEvent(event) {
      stopTapDemoEvent(event);
    }

    function advanceAfterClaim(event) {
      freezeIntro(event);
      if (advancingRef.current) return;
      advancingRef.current = true;
      haptic('advance', { key: 'flow-demo-advance' });
      props.onContinue();
    }

    function handlePress(event) {
      if (advancingRef.current) {
        stopPressEvent(event);
        return;
      }
      if (!claimTapDemoPress(event)) return;
      advanceAfterClaim(event);
    }

    function handleContinue(event) {
      if (advancingRef.current) {
        stopPressEvent(event);
        return;
      }

      if (!claimTapDemoPress(event)) return;
      advanceAfterClaim(event);
    }

    return jsx('button', {
      type: 'button',
      ref: screenRef,
      className: 'screen flow-demo-alarm'
        + (isWakeTimeDemo ? ' flow-demo-alarm--wake' : '')
        + (isWakeTimeDemo ? '' : ' flow-demo-alarm--dock')
        + ((forceSettled || isExiting) ? ' flow-demo-alarm--settled flow-demo-alarm--exiting' : ''),
      onClick: handleContinue,
      onMouseDownCapture: handlePress,
      onPointerDownCapture: handlePress,
      onTouchStartCapture: handlePress,
      'aria-label': ariaLabel,
      children: [
        introVisible ? jsx('span', {
          className: 'flow-demo-alarm__intro-time',
          ref: introRef,
          'aria-hidden': true,
          children: [
            jsx('span', { className: 'flow-demo-alarm__intro-clock', children: '⏰' }),
            wakeTime + '.'
          ]
        }) : null,
        jsx('div', {
          className: 'flow-demo-alarm__eyebrow',
          children: eyebrow
        }),
        jsx('h2', {
          className: 'flow-demo-alarm__title',
          children: isWakeTimeDemo ? [
            jsx('span', {
              className: 'flow-demo-alarm__time' + (!timeSettled ? ' flow-demo-alarm__time--waiting' : ''),
              ref: timeRef,
              children: [
                jsx('span', { className: 'flow-demo-alarm__clock', 'aria-hidden': true, children: '⏰' }),
                wakeTime + '.'
              ]
            }),
            jsx('span', { className: 'flow-demo-alarm__title-rest', children: ' Your alarm rings.' })
          ] : jsx('span', { className: 'flow-demo-alarm__title-rest', children: renderSegments(titleSegments) })
        }),
        i.subtitle ? jsx('p', { className: 'flow-demo-alarm__subtitle', children: i.subtitle }) : null,
        jsx('div', {
          className: 'flow-demo-alarm__image-shell',
          children: [
            jsx('span', { className: 'flow-demo-alarm__halo', 'aria-hidden': true }),
            jsx('img', {
              className: 'flow-demo-alarm__image',
              src: imageSrc,
              alt: '',
              onError: function (event) {
                if (event.currentTarget.dataset.triedLocal === 'true') return;
                event.currentTarget.dataset.triedLocal = 'true';
                event.currentTarget.src = localImageSrc;
              }
            })
          ]
        }),
        jsx('span', {
          className: 'flow-demo-alarm__tap',
          children: i.cta || 'tap to continue'
        })
      ]
    });
  }

  function AlarmSoundsInterstitial(props) {
    var i = props.interstitial;
    var sounds = i.sounds || [];
    var playingRef = useState(null);
    var playingIndex = playingRef[0];
    var setPlayingIndex = playingRef[1];
    var audioRef = useRef(null);
    var playRequestRef = useRef(0);

    useEffect(function () {
      return function () {
        stopSound();
      };
    }, []);

    function stopSound() {
      playRequestRef.current += 1;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        haptic('back', { key: 'alarm-preview-stop' });
      }
      setPlayingIndex(null);
    }

    async function toggleSound(sound, idx) {
      if (playingIndex === idx && audioRef.current) {
        stopSound();
        return;
      }

      stopSound();
      var requestId = playRequestRef.current + 1;
      playRequestRef.current = requestId;

      var src = window.resolveAsset ? window.resolveAsset(sound.src) : sound.src;
      try {
        var audio = new Audio(src);
        audio.preload = 'auto';
        audioRef.current = audio;
        audio.onended = function () {
          if (audioRef.current === audio) stopSound();
        };
        audio.onerror = function () {
          if (audioRef.current === audio) stopSound();
        };
        await audio.play();
        haptic('advance', { key: 'alarm-preview-play' });
        if (playRequestRef.current === requestId) setPlayingIndex(idx);
      } catch (e) {
        stopSound();
      }
    }

    function continueFromSounds() {
      if (audioRef.current) {
        stopSound();
      } else {
        setPlayingIndex(null);
      }
      haptic('advance', { key: 'alarm-sounds-continue' });
      props.onContinue();
    }

    return jsx('div', {
      className: 'screen alarm-sounds-screen',
      children: [
        jsx('div', {
          className: 'alarm-sounds__header',
          children: jsx('h2', {
            className: 'q-title alarm-sounds__title',
            children: i.title
          })
        }),

        jsx('div', {
          className: 'alarm-sounds__card',
          children: [
            jsx('div', {
              className: 'alarm-sounds__section-label',
              children: i.sectionLabel || 'Popular'
            }),
            jsx('div', {
              className: 'alarm-sounds__list',
              children: sounds.map(function (sound, idx) {
                var isPlaying = playingIndex === idx;
                return jsx('button', {
                  key: 'sound-' + idx,
                  type: 'button',
                  className: 'alarm-sound-row' + (isPlaying ? ' is-playing' : ''),
                  onClick: function () { toggleSound(sound, idx); },
                  'aria-label': (isPlaying ? 'Pause ' : 'Play ') + sound.title,
                  children: [
                    jsx('span', {
                      className: 'alarm-sound-row__text',
                      children: [
                        jsx('span', { className: 'alarm-sound-row__title', children: sound.title }),
                        jsx('span', { className: 'alarm-sound-row__category', children: sound.category })
                      ]
                    }),
                    jsx('span', {
                      className: 'alarm-sound-row__play',
                      'aria-hidden': true,
                      children: isPlaying
                        ? jsx('span', { className: 'alarm-sound-row__pause-icon' })
                        : jsx('svg', {
                            viewBox: '0 0 18 18',
                            children: jsx('path', { d: 'M6.5 4.2v9.6L13.8 9z', fill: 'currentColor' })
                          })
                    })
                  ]
                });
              })
            })
          ]
        }),

        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            className: 'btn btn--primary btn--light',
            onClick: continueFromSounds,
            children: i.cta || 'Continue'
          })
        })
      ]
    });
  }

  function MorningWithFlowInterstitial(props) {
    var i = props.interstitial;
    var rows = i.rows || [];

    // Resolve the base wake time from the wake_time answer (falls back to
    // baseDefault). Each row's `offset` is added to produce its clock time.
    var base = resolveWakeBase(i, props.answers || {});

    return jsx('div', {
      className: 'screen mwf-screen',
      children: [
        jsx('h2', { className: 'interstitial__title', children: i.title }),

        jsx('div', {
          className: 'mwf-card',
          children: jsx('div', {
            className: 'mwf-grid',
            children: rows.map(function (r, idx) {
              var isLast = idx === rows.length - 1;
              var rowTime = (typeof r.offset === 'number') ? formatClock(base + r.offset) : r.time;
              return jsx('div', {
                key: 'r' + idx,
                className: 'mwf-row',
                style: { animationDelay: (200 + idx * 180) + 'ms' },
                children: [
                  // Left rail: green check dot + connecting line (except last row)
                  jsx('div', {
                    className: 'mwf-rail',
                    children: [
                      jsx('span', {
                        className: 'mwf-dot',
                        children: jsx('svg', {
                          width: 12, height: 12, viewBox: '0 0 12 12', 'aria-hidden': true,
                          children: jsx('path', {
                            d: 'M 2.5 6.2 L 5 8.5 L 9.5 3.5',
                            stroke: '#fff', strokeWidth: 1.6, fill: 'none',
                            strokeLinecap: 'round', strokeLinejoin: 'round'
                          })
                        })
                      }),
                      !isLast ? jsx('span', { className: 'mwf-line' }) : null
                    ]
                  }),
                  // Right: time + description
                  jsx('div', {
                    className: 'mwf-text',
                    children: [
                      jsx('span', { className: 'mwf-time', children: rowTime }),
                      jsx('span', { className: 'mwf-desc', children: r.text })
                    ]
                  })
                ]
              });
            })
          })
        }),

        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            className: 'btn btn--primary btn--light',
            onClick: function () { haptic("advance"); props.onContinue(); },
            children: i.cta || 'Continue'
          })
        })
      ]
    });
  }

  // ------------------------------------------------------------
  // BestVersionInterstitial — "Time to become the best version"
  // GREEN-dot timeline (per Figma): hero row "6:30 AM · You're up,
  // first try." plus three benefit cards (No rushing / Exercising
  // / Reading) with orange icon badges.
  // ------------------------------------------------------------
  function BestVersionInterstitial(props) {
    var i = props.interstitial;
    var hero = i.hero || {};
    var cards = i.cards || [];
    // Personalize the hero time from the wake_time answer (same base the
    // morning-with-Flow timeline uses); falls back to hero.time.
    var heroTime = hero.time;
    var wakeBase = resolveWakeBase(i, props.answers || {});
    if (i.baseFrom) heroTime = formatClock(wakeBase);
    var leaveTime = formatClock(wakeBase + 30);

    return jsx('div', {
      className: 'screen best-version-screen',
      children: [
        // 2-line title (28px Inter Display SemiBold per Figma)
        jsx('h2', {
          className: 'best-version__title',
          children: [
            jsx('span', { className: 'best-version__title-line', children: i.titleLine1 }),
            jsx('span', { className: 'best-version__title-line', children: i.titleLine2 })
          ]
        }),

        // Hero card — gray bg with ORANGE icon, time, and result text
        jsx('div', {
          className: 'best-version__hero',
          children: [
            jsx('div', {
              className: 'best-version__hero-left',
              children: [
                jsx('span', { className: 'best-version__hero-icon', children: hero.icon || '⏰' }),
                jsx('span', { className: 'best-version__hero-time', children: heroTime })
              ]
            }),
            jsx('span', { className: 'best-version__hero-result', children: hero.result })
          ]
        }),

        // 3 benefit cards — gray bg, orange icon, white title + dim subtitle
        jsx('div', {
          className: 'best-version__cards',
          children: cards.map(function (c, idx) {
            return jsx('div', {
              key: String(idx),
              className: 'best-version__card',
              style: { animationDelay: (320 + idx * 140) + 'ms' },
              children: [
                    jsx('span', { className: 'best-version__card-icon', children: c.icon }),
                jsx('div', {
                  className: 'best-version__card-body',
                  children: [
                    jsx('span', { className: 'best-version__card-title', children: c.title }),
                    jsx('span', {
                      className: 'best-version__card-sub',
                      children: c.subtitleFromWake
                        ? "You'll have until " + leaveTime + ' before you need to leave.'
                        : c.subtitle
                    })
                  ]
                })
              ]
            });
          })
        }),

        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            className: 'btn btn--primary btn--light',
            onClick: function () { haptic("advance"); props.onContinue(); },
            children: i.cta || 'Continue'
          })
        })
      ]
    });
  }

  // ------------------------------------------------------------
  // YouVsFlowInterstitial — Figma 11821:14877
  // Side-by-side comparison: dim translucent left card vs orange
  // right card, each with 4 pill tags. Title splits across two
  // lines: the "right now: 10%" line is dimmed (40% white) and the
  // "with Flow: 100%" line is full white.
  // ------------------------------------------------------------
  function YouVsFlowInterstitial(props) {
    var i = props.interstitial;
    var left = i.left || { title: '', pills: [] };
    var right = i.right || { title: '', pills: [] };
    return jsx('div', {
      className: 'screen yvf-screen',
      children: [
        jsx('h2', {
          className: 'yvf-title',
          children: [
            jsx('span', { className: 'yvf-title__line yvf-title__line--dim',  children: i.titleDim  }),
            jsx('span', { className: 'yvf-title__line yvf-title__line--full', children: i.titleFull })
          ]
        }),

        jsx('div', {
          className: 'yvf-grid',
          children: [
            // LEFT — "You right now"
            jsx('div', {
              className: 'yvf-col yvf-col--now',
              children: [
                jsx('span', { className: 'yvf-col__title yvf-col__title--dim', children: left.title }),
                jsx('div', {
                  className: 'yvf-inner yvf-inner--now',
                  children: left.pills.map(function (p, idx) {
                    return jsx('span', { key: 'l' + idx, className: 'yvf-pill yvf-pill--now', children: p });
                  })
                })
              ]
            }),
            // RIGHT — "You with Flow"
            jsx('div', {
              className: 'yvf-col yvf-col--flow',
              children: [
                jsx('span', { className: 'yvf-col__title yvf-col__title--full', children: right.title }),
                jsx('div', {
                  className: 'yvf-inner yvf-inner--flow',
                  children: right.pills.map(function (p, idx) {
                    return jsx('span', { key: 'r' + idx, className: 'yvf-pill yvf-pill--flow', children: p });
                  })
                })
              ]
            })
          ]
        }),

        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            className: 'btn btn--primary btn--light',
            onClick: function () { haptic("advance"); props.onContinue(); },
            children: i.cta || 'Continue'
          })
        })
      ]
    });
  }
})();
